import { alpha, createTheme, ThemeOptions } from "@mui/material/styles";
import { softChipStyle, type ChipTone } from "../statusChip";
import { sharedDarkBrand, sharedLightBrand } from "../brandTokens";

declare module "@mui/material/styles" {
  interface Palette {
    selected: Palette["primary"];
    purple: Palette["primary"];
    form?: {
      label: string;
      border: string;
      readOnly: {
        text: string;
        border: string;
        label: string;
      };
    };
  }
  interface PaletteOptions {
    selected?: PaletteOptions["primary"];
    purple?: PaletteOptions["primary"];
    form?: {
      label: string;
      border: string;
      readOnly: {
        text: string;
        border: string;
        label: string;
      };
    };
  }

  interface TypeText {
    label: string;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    purple: true;
  }
}

const commonPalette = {
  primary: {
    light: "#7BC7C1", // Lighter Teal Green
    main: "#52B5AD", // Teal Green
    dark: "#3A8F88", // Darker Teal Green
    contrastText: "#ffffff",
  },
  secondary: {
    light: "#3E8CAF", // Lighter Dark Blue
    main: "#2A6D92", // Dark Blue
    dark: "#1C4E6B", // Darker Dark Blue
    contrastText: "#ffffff",
  },
  error: {
    light: "#ff5252",
    main: "#f44336",
    dark: "#d32f2f",
    contrastText: "#fff",
  },
  purple: {
    light: "#9c27b0",
    main: "#7b1fa2",
    dark: "#4a148c",
    contrastText: "#fff",
  },
  selected: {
    light: "#E0F2F1", // Light Teal
    main: "#B2DFDB", // Medium Teal
    dark: "#80CBC4", // Dark Teal
    contrastText: "#00796B", // Dark Green
  },
  form: {
    label: "rgba(0, 0, 0, 0.6)",
    border: "rgba(0, 0, 0, 0.23)",
    readOnly: {
      text: "rgba(0, 0, 0, 0.87)",
      border: "rgba(0, 0, 0, 0.23)",
      label: "rgba(0, 0, 0, 0.6)",
    },
  },
};

const typography: ThemeOptions["typography"] = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontWeight: 700,
    fontSize: "2.5rem",
    letterSpacing: "-0.02em",
  },
  h2: {
    fontWeight: 700,
    fontSize: "2rem",
    letterSpacing: "-0.01em",
  },
  h3: {
    fontWeight: 600,
    fontSize: "1.75rem",
  },
  h4: {
    fontWeight: 600,
    fontSize: "1.5rem",
  },
  h5: {
    fontWeight: 600,
    fontSize: "1.25rem",
  },
  h6: {
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  subtitle2: {
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: "1rem",
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  body2: {
    fontSize: "0.875rem",
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
  },
  button: {
    textTransform: "none",
    fontWeight: 500,
    letterSpacing: "0.02857em",
  },
  caption: {
    fontSize: "0.75rem",
    lineHeight: 1.5,
    letterSpacing: "0.03333em",
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: "0.08333em",
    textTransform: "uppercase",
  },
};

/**
 * Form-label metrics, and the notch that has to match them.
 *
 * MUI cuts the outlined field's border gap with a `<legend>` at `0.75em` of the INPUT's font — a
 * default that is only right while the label is also 1rem. This theme's label is smaller, so the
 * notch has to be re-derived from it: the floating label renders at `LABEL_FONT_REM × LABEL_SHRINK`,
 * and the notch must be cut to exactly that. Derived rather than written out, because the two
 * drifting apart is invisible in code and obvious on screen (NEW-78).
 */
const LABEL_FONT_REM = 0.875;
const LABEL_SHRINK = 0.75; // MUI's floating-label transform: scale(0.75)
const LABEL_FONT_SIZE = `${LABEL_FONT_REM}rem`;
const LABEL_FONT_WEIGHT = 500;
const NOTCH_FONT_SIZE = `${LABEL_FONT_REM * LABEL_SHRINK}em`;

const lightComponents: ThemeOptions["components"] = {
  MuiCssBaseline: {
    // Function form so one definition serves both modes — the scrollbar is derived from
    // text.primary, which inverts with the palette.
    styleOverrides: (theme) => ({
      // Honor "reduce motion" globally: neutralize transitions/animations for
      // users who ask for it, instead of gating each animated component.
      "@media (prefers-reduced-motion: reduce)": {
        "*, *::before, *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          transitionDuration: "0.01ms !important",
          scrollBehavior: "auto !important",
        },
      },
      // The platform scrollbar is a light grey slab, which on the dark navy is one of the
      // brightest things on screen — it drew the eye more than the content it scrolled. A
      // translucent thumb on a transparent track recedes in both modes.
      "*": {
        scrollbarWidth: "thin",
        scrollbarColor: `${alpha(theme.palette.text.primary, 0.22)} transparent`,
      },
      "*::-webkit-scrollbar": { width: 10, height: 10 },
      "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.text.primary, 0.18),
        borderRadius: 8,
        // Transparent border + content-box clip insets the thumb, so it reads as a slim pill
        // rather than filling the gutter edge to edge.
        border: "2px solid transparent",
        backgroundClip: "content-box",
      },
      "*::-webkit-scrollbar-thumb:hover": {
        backgroundColor: alpha(theme.palette.text.primary, 0.34),
      },
      "*::-webkit-scrollbar-corner": { backgroundColor: "transparent" },
    }),
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: LABEL_FONT_SIZE,
        fontWeight: LABEL_FONT_WEIGHT,
        // A field in error already shows it three ways: a red underline, red helper text, and
        // `aria-invalid` for a screen reader. Turning the LABEL red as well restates it a fourth
        // time in the loudest place on the row — the field's name — which reads as though the name
        // itself were wrong. The label stays neutral; the error keeps saying what is actually
        // wrong, in the place that says it.
        "&.Mui-error": {
          color: "inherit",
        },
        // Focus still wins over error, so tabbing to the field to fix it looks like a field you are
        // editing rather than one still being scolded.
        "&.Mui-error.Mui-focused": {
          color: "primary.main",
        },
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        fontSize: "1rem",
        fontWeight: 400,
      },
    },
  },
  // Standard-variant fields (the registration wizard and every `fieldVariant="standard"` form) put
  // the shrunk label above the value. MUI reserves a flat 16px for it — a figure that assumes the
  // default label metrics, NOT this theme's larger `MuiFormLabel` (0.875rem / 500). The label is
  // therefore taller than the space was sized for and the extra lands as a gap between the caption
  // and the value it belongs to, loosening the pair that should read as one thing.
  MuiInput: {
    styleOverrides: {
      formControl: {
        "label + &": {
          marginTop: 12,
        },
      },
    },
  },
  // The OUTLINED variant's notch, which is a different bug with the same cause as the one above.
  //
  // MUI cuts the gap in the border with a <legend> sized at `0.75em` — em being the INPUT's font
  // (1rem = 16px), so the notch is cut for 12px of text. The floating label is not 12px: it is this
  // theme's `MuiFormLabel` size shrunk by the same 0.75 transform, i.e. 0.875rem × 0.75 = 10.5px.
  // The notch is therefore cut ~14% wider than the word it is meant to clear, and the surplus shows
  // as a gap between the label and where the border resumes.
  //
  // 0.65625em == 0.875 × 0.75, so the notch is sized from the label's REAL width. It is tied to the
  // MuiFormLabel size above — change one and the other must follow, which `theme.labelNotch.test.ts`
  // enforces rather than leaving to whoever edits it next.
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        "& legend": {
          fontSize: NOTCH_FONT_SIZE,
        },
      },
    },
  },
};

const darkComponents: ThemeOptions["components"] = {
  ...lightComponents,
  // MUI fakes elevation on dark Paper by painting a white gradient over it, scaled by elevation.
  // At the elevations dialogs, menus and popovers use that is ~12-16% white, which washes the
  // navy out to a flat grey slate — so every overlay looked like it came from a different design
  // system than the page behind it. Drop the gradient and let the palette + a hairline border do
  // the separating. Scoped to the elevated surfaces: cards and panels sit at low elevation where
  // the overlay is imperceptible, and are left alone.
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundImage: "none",
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundImage: "none",
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundImage: "none",
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  // Soften every filled semantic chip (success/warning/error/info) into the shared tinted style,
  // so status chips across the app read calmly on the navy instead of solid bright fills.
  MuiChip: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const soft = ["success", "warning", "error", "info"];
        const color = ownerState.color ?? "default";
        if (ownerState.variant !== "outlined" && soft.includes(color)) {
          return softChipStyle(theme, color as ChipTone);
        }
        return {};
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
    },
    variants: [
      {
        props: { variant: "contained", color: "secondary" },
        style: {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.2)",
          },
        },
      },
    ],
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255, 255, 255, 0.23)",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(255, 255, 255, 0.4)",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#22D3D6",
        },
        "& input": {
          color: "#ffffff",
        },
        "&::placeholder": {
          color: "rgba(255, 255, 255, 0.5)",
        },
      },
    },
  },
  // NOTE: this REPLACES the light MuiFormLabel block rather than merging with it (darkComponents
  // spreads lightComponents, then redefines this key), so every property the light one sets has to
  // be repeated here. It did not repeat the size, which is why dark-mode labels were 1rem while
  // light ones were 0.875rem — a difference nobody had noticed, and one that would now also cut the
  // outlined notch too narrow for the label sitting in it.
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: LABEL_FONT_SIZE,
        fontWeight: LABEL_FONT_WEIGHT,
        color: "rgba(255, 255, 255, 0.7)",
        "&.Mui-focused": {
          color: "#22D3D6",
        },
        "&.Mui-error": {
          color: "inherit",
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiInputLabel-root": {
          color: "rgba(255, 255, 255, 0.7)",
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: "#22D3D6",
        },
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "rgba(255, 255, 255, 0.23)",
          },
          "&:hover fieldset": {
            borderColor: "rgba(255, 255, 255, 0.4)",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#22D3D6",
          },
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    ...commonPalette,
    background: {
      default: "#F5F7F8",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#2A6D92",
      label: "rgba(0, 0, 0, 0.6)",
    },
    brand: sharedLightBrand,
  },
  typography,
  components: lightComponents,
});

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    ...commonPalette,
    // Dark palette matched to the dark login page: deep navy surfaces, CerebraLinked teal + an
    // electric-blue secondary, near-white text.
    primary: {
      light: "#22D3D6",
      main: "#16BCC0",
      dark: "#0B8385",
      contrastText: "#04141C",
    },
    secondary: {
      light: "#3FA3CC",
      main: "#1E7CAD",
      dark: "#0D4C6B",
      contrastText: "#ffffff",
    },
    background: {
      default: "#0A1728",
      paper: "#122139",
    },
    text: {
      primary: "#F4FBFC",
      secondary: "#A8C7CE",
      label: "rgba(255, 255, 255, 0.7)",
    },
    form: {
      label: "rgba(255, 255, 255, 0.7)",
      border: "rgba(255, 255, 255, 0.23)",
      readOnly: {
        text: "#ffffff",
        border: "rgba(255, 255, 255, 0.23)",
        label: "rgba(255, 255, 255, 0.7)",
      },
    },
    error: {
      light: "#ff7171", // Lighter, more muted red for dark mode
      main: "#ff5252", // Softer red that works better on dark backgrounds
      dark: "#ff4444", // Slightly brighter to maintain visibility
      contrastText: "#ffffff",
    },
    // Many components use grey.50 / grey.100 as "light panel" backgrounds (page canvases,
    // inset sections, row hovers). MUI does not darken the grey scale in dark mode, so those
    // render near-white on the dark canvas. Remap the two light steps to dark surfaces so every
    // token-based grey panel goes dark without editing each call site.
    // grey.50/100 back "light panels" — remapped to navy so they don't render near-white.
    // grey.900 is what the AppBar, left rail, and content canvas use for their surface, so it
    // carries the deep navy and keeps the whole shell cohesive with the content.
    grey: {
      50: "#16273F",
      100: "#1B2E48",
      900: "#0A1728",
    },
    brand: sharedDarkBrand,
  },
  typography,
  components: darkComponents,
});
